/**
 * Settings - API 키 설정 페이지
 *
 * Phase 1 리팩토링: 컴포넌트 분리
 * - ExistingSettings: 기존 설정 표시 컴포넌트
 * - SettingsForm: API 키 입력 폼 컴포넌트
 */
import React, { useState, useEffect } from 'react';
import ExistingSettings from '../components/settings/ExistingSettings';
import SettingsForm from '../components/settings/SettingsForm';
import './Settings.css';
import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiFetch';

interface APIKeysResponse {
  has_api_key: boolean;
  has_secret_key: boolean;
  crypto_enabled: boolean;
  api_key_preview: string | null;
}

const Settings: React.FC = () => {
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [existingSettings, setExistingSettings] = useState<APIKeysResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/settings/keys`);
      if (!response.ok) {
        throw new Error(`API 오류: ${response.status}`);
      }
      const data: APIKeysResponse = await response.json();
      setExistingSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'API 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // API 키가 입력되었으면 POST, 아니면 PUT
      const method = apiKey && secretKey ? 'POST' : 'PUT';
      const body = apiKey && secretKey
        ? { alpaca_api_key: apiKey, alpaca_secret_key: secretKey, crypto_enabled: false }
        : { crypto_enabled: false };

      const response = await apiFetch(`${API_BASE_URL}/api/settings/keys`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // 서버에서 반환한 상세 에러 메시지 추출
        const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
        throw new Error(errorData.detail || `저장 실패: ${response.status}`);
      }

      const data: APIKeysResponse = await response.json();
      setExistingSettings(data);
      setApiKey('');
      setSecretKey('');
      setSuccess(true);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '저장 중 오류 발생';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <h1>연동/보안 설정</h1>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">설정이 저장되었습니다</div>}

      {existingSettings?.has_api_key && (
        <ExistingSettings settings={existingSettings} />
      )}

      <SettingsForm
        apiKey={apiKey}
        setApiKey={setApiKey}
        secretKey={secretKey}
        setSecretKey={setSecretKey}
        saving={saving}
        onSubmit={handleSave}
      />

      <div className="info-box">
        <h4>안내</h4>
        <ul>
          <li>Alpaca Paper Trading API 키가 필요합니다</li>
          <li>API 키는 Fernet 암호화로 안전하게 저장됩니다</li>
          <li>암호화폐 거래 기능은 폐기되었습니다. US/KR 주식 경로만 운영합니다</li>
        </ul>
      </div>
    </div>
  );
};

export default Settings;
